import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { tagHotjarRecording, getCategoryCards } from '../../helpers/util'
import { logErrorOnServer } from '../../helpers/serverLogging'
import useRequest from '../../hooks/useRequest'
import useUsedEquipment from '../../hooks/useUsedEquipment'
import ContentLayout from '../../components/ContentLayout/ContentLayout'
import MainHeading from '../../components/MainHeading/MainHeading'
import Button from '../../components/Button/Button'
import { DocumentAddIcon } from '@heroicons/react/outline'
import CardInfo from '../../components/CardInfo/CardInfo'
import useGlobalLoading from '../../hooks/useGlobalLoading'
import UsedEquipmentItem from './components/UsedEquipmentItem'
import Pagination from '../../components/Table/components/Pagination'
import UsedEquipmentFiltersModal from './components/filters/UsedEquipmentFiltersModal'
import {
    getDeals,
    getInitialFilters,
    getMaxCapacity,
    getMinCapacity,
    getSelectedField,
} from '../../helpers/usedEquipmentFiltersUtils'
import { Link } from 'react-router-dom'
import AppRoutes from '../../routes/appRoutes'

const logError = logErrorOnServer('Used Equipment Search')

const cards = getCategoryCards()

const UsedEquipmentRequestPage = () => {
    const { saveUsedEquipmentFilters, filters } = useUsedEquipment()

    const [appliedFilters, setAppliedFilters] = useState(
        getInitialFilters(filters),
    )

    const minHeight = filters.minHeight
    const maxHeight = filters.maxHeight
    const pageSize = filters.pageSize
    const scrolleableRef = useRef()

    const [filterModalOpen, setFilterModalOpen] = useState(false)
    const [requestUUID, setRequestUUID] = useState(filters.requestUUID)
    const [pageNumber, setPageNumber] = useState(filters.pageNumber)
    const [equipmentRecords, setEquipmentRecords] = useState([])
    const [totalEquipmentFound, setTotalEquipmentFound] = useState(0)
    const [researchEquipment, setResearchEquipment] = useState(false)
    const { handleSearchUsedEquipment } = useRequest()
    const { changeLoading } = useGlobalLoading()

    const toggleResearchEquipment = () => {
        setResearchEquipment(!researchEquipment)
    }

    useEffect(() => {
        searchEquipment()
    }, [researchEquipment])

    const onSearchEquipmentSuccess = (response, newData) => {
        changeLoading(false)
        setEquipmentRecords(response.data.equipment)
        setRequestUUID(response.data.requestUUID)
        setPageNumber(response.data.pageNumber)
        setTotalEquipmentFound(response.data.totalRecordsFound)
        const { card, newFilters } = newData
        const filters = {
            card: {
                equipmentType: card.equipmentType,
                equipmentSubType: card.equipmentSubtype,
            },
            selectedCategory: card?.category,
            deals: newFilters?.deals,
            fuelType: newFilters?.fuelType,
            minCapacity: newFilters?.minCapacity,
            maxCapacity: newFilters?.maxCapacity,
            minHeight: minHeight,
            maxHeight: maxHeight,
            requestUUID: response.data.requestUUID,
            pageNumber: pageNumber,
            pageSize: pageSize,
        }
        saveUsedEquipmentFilters(filters)
    }

    const onSearchEquipmentError = response => {
        tagHotjarRecording('API Error')
        const message = response.data ? response.data.message : 'Search failed'
        changeLoading(false)
        logError(message)
    }

    const onCleanFilters = () => {
        setRequestUUID('')
    }

    const searchEquipment = () => {
        changeLoading(true)
        const selectedCategory = getSelectedField(appliedFilters, 'Class')
            ?.filterName
        const selectedFuelType = getSelectedField(appliedFilters, 'Fuel Type')
            ?.filterName
        const selectedDeals = getDeals(appliedFilters)
        const selectedMinCapacity = getMinCapacity(appliedFilters)
        const selectedMaxCapacity = getMaxCapacity(appliedFilters)
        const card = cards.find(card => card.name == selectedCategory)

        const paramsToSearch = {
            card,
            fuelType: selectedFuelType,
            deals: selectedDeals,
            minCapacity: selectedMinCapacity,
            maxCapacity: selectedMaxCapacity,
            minHeight,
            maxHeight,
            pageNumber,
            pageSize,
            requestUUID,
        }

        handleSearchUsedEquipment(
            paramsToSearch,
            {
                card: card,
                newFilters: {
                    fuelType: selectedFuelType,
                    deals: selectedDeals,
                    minCapacity: selectedMinCapacity,
                    maxCapacity: selectedMaxCapacity,
                },
            },
            onSearchEquipmentSuccess,
            onSearchEquipmentError,
        )
    }

    const changePage = value => {
        setPageNumber(value)
        toggleResearchEquipment()
    }

    const renderItems = () => {
        return (
            <div className="w-full max-w-full h-auto grid lg:grid-cols-4 md:grid-cols-2 grid-cols-1 gap-x-6 md:gap-y-8 gap-y-4">
                {equipmentRecords?.map((item, index) => {
                    return (
                        <UsedEquipmentItem
                            equipment={item}
                            key={`item-${index}`}
                        />
                    )
                })}
            </div>
        )
    }

    const renderFilters = () => {
        return (
            <UsedEquipmentFiltersModal
                key="filterModal"
                open={filterModalOpen}
                setOpen={setFilterModalOpen}
                onFilter={fil => {
                    setAppliedFilters(fil)
                    setRequestUUID('')
                    toggleResearchEquipment()
                }}
                initialFilters={filters}
                filtersGroup={appliedFilters}
                onClean={onCleanFilters}
            />
        )
    }

    return (
        <ContentLayout>
            <MainHeading
                title="Equipment Finder"
                actions={[
                    <Link
                        key="createEquipmentRequest"
                        to={AppRoutes.usedEquipmentRequestForm()}
                        className="md:w-auto w-full flex-grow"
                    >
                        <Button
                            text="Create Equipment Request"
                            variant="primary"
                            className="w-full"
                            icon={
                                <DocumentAddIcon className="text-yellow stroke-current stroke-2 w-6 h-6 mr-2" />
                            }
                        />
                    </Link>,
                ]}
            />
            <div
                ref={scrolleableRef}
                className="w-full h-auto flex-grow flex items-start justify-start max-h-full overflow-auto md:px-6 md:py-8 px-4 py-4"
            >
                <CardInfo
                    title="Used Equipment Finder"
                    subTitle="Choose from our large selection of used equipment inventory"
                    isExpanded={false}
                    headerActions={[renderFilters()]}
                    content={renderItems()}
                    titleClass="md:w-auto w-full"
                    actions={[
                        <Pagination
                            key="pagination"
                            totalResults={totalEquipmentFound}
                            pageSize={pageSize}
                            onChangePage={page => {
                                if (scrolleableRef?.current) {
                                    scrolleableRef?.current?.scrollTo({
                                        top: 0,
                                        behavior: 'smooth',
                                    })
                                }
                                changePage(page)
                            }}
                        />,
                    ]}
                />
            </div>
        </ContentLayout>
    )
}

UsedEquipmentRequestPage.propTypes = {
    location: PropTypes.shape({
        search: PropTypes.string,
        state: PropTypes.shape({
            source: PropTypes.string,
        }),
    }),
}

export default UsedEquipmentRequestPage
